import queryString from "query-string";
import { useServerList } from "~/hooks/useServerList";
import { TableAccessLevel } from "~/pages/datasets/types/enums/access-level";
import { V1TableSearchResult } from "~/types/interfaces/catalogTables";

const { VITE_CATALOG_API } = import.meta.env;

/**
 * Tables fetched per "Load more". Comfortably fills the picker without approaching the
 * endpoint's 1000-row ceiling, so a wide schema costs several small pages rather than
 * one oversized response.
 */
export const SCHEMA_TABLES_PAGE_SIZE = 100;

/**
 * Pages the tables in a single schema, appending a page per `loadMore()`.
 *
 * Sorts by name because table names are unique within a schema, making it a total
 * order — relevance ordering has no unique tiebreaker at the top level, so offset
 * paging could duplicate and skip rows between pages. Requests `aggregation=none`
 * since the schema facet is fetched once by useTableSchemas rather than recomputed on
 * every page.
 *
 * `searchTerm` must already be debounced; the caller owns the timing so this hook and
 * useTableSchemas share one timer.
 */
const useSchemaTables = (schema: string, searchTerm: string, accessLevel?: TableAccessLevel) => {
  const qs = queryString.stringify({
    // The endpoint does not normalize a blank term, so send the match-everything
    // wildcard explicitly.
    query_term: searchTerm || "*",
    database_schema: schema,
    sort: "name",
    aggregation: "none",
    // Only include min_access_level when an accessLevel is provided
    ...(accessLevel && { min_access_level: accessLevel }),
  });

  // useServerList appends limit/offset and resets to the first page whenever `path`
  // changes, so a new search term restarts paging rather than continuing it.
  const list = useServerList<V1TableSearchResult>({
    path: `${VITE_CATALOG_API}/v1/tables/search?${qs}`,
    pageSize: SCHEMA_TABLES_PAGE_SIZE,
  });

  return {
    ...list,
    // Drives the "Load more (N remaining)" label, so truncation is visible rather than
    // being mistaken for the end of the list.
    remainingCount: Math.max(0, list.total - list.items.length),
  };
};

export default useSchemaTables;
